import { Card, CardContent, Grid } from '@mui/material';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { DeleteButton } from '../../../components/delete-button.component';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { useFormContext, useWatch } from 'react-hook-form';
import { Icons } from '../../../assets/icons';
import { ApiResponse } from '../../../models/api-response';
import { useLoadData } from '../../../hooks/useLoadData';
import { useEffect, useRef, useState } from 'react';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { FormFormattedInput } from '../../../components/hook-form/form-formatted-input.component';
import { FormSelectDynamic } from '../../../components/hook-form/form-select.dynamic.component';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { municipalityApiKeyStore } from '../../../api/masters/municipality-api-key-store';
import { stateApiKeyStore } from '../../../api/states-api-key-store';
import { ZIPCODE_FORMAT } from '../../../constants';
import { countyApiKeyStore } from '../../../api/county-api-key-store';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { FormSelect } from '../../query-builder/hooks-form-querybuilder/form-select.component';
import { getAddressAbbreviationError } from '../../../utils/abbreviations.util';

const lotTypeOption = [
  {
    value: 'TH',
    label: 'Townhome (TH)',
  },
  {
    value: 'SFR',
    label: 'Single-Family Residential (SFR)',
  },
];

const lotTypeCMOption = [
  {
    value: 'CM',
    label: 'Commercial (CM)',
  },
];

interface BuildingFloorUnitFormProps {
  name: string;
  index?: number;
  onRemove?: (index: number) => void;
  isEnabled?: boolean;
  syncWithDux: any;
  projectTypeValue: any;
  existingData: any;
}

export const ScatteredUnitFormWithDefaultValue = ({
  name,
  index,
  onRemove,
  syncWithDux,
  isEnabled = false,
  projectTypeValue,
  existingData,
}: BuildingFloorUnitFormProps) => {
  const { getValues, setValue, watch, setError, clearErrors } = useFormContext();

  const { data, isLoading } = useLoadData<ApiResponse<any>>(
    projectsApiKeyStore.generateLotName(getValues('projectID')),
    isEnabled
  );

  const jobsiteRef = useRef<string | null>(null);
  const [jobNumberWarning, setJobNumberWarning] = useState<string | null>(null);

  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  useWatch({
    name: `${name}.jobsite` || [],
  });

  useEffect(() => {
    if (data && data?.data) {
      setValue(`${name}.jobsite`, data?.data?.unitName);
      jobsiteRef.current = data?.data?.unitName;
    }
  }, [data]);

  const watchValues = watch();

  const handleUnitNameChange = (value: string) => {
    const initialJobsite = jobsiteRef.current || getValues(`${name}.jobsite`);
    const newJobsite = `${initialJobsite}${value}`.trim();
    setValue(`${name}.jobsite`, newJobsite);

    const allJobsites = existingData?.map((u: any) => normalize(u?.jobsite));
    const isJobsiteDuplicate = allJobsites?.includes(normalize(newJobsite));
    if (isJobsiteDuplicate) {
      setError(`${name}.jobsite`, {
        type: 'manual',
        message: 'Jobsite already exists.',
      });
    } else {
      clearErrors(`${name}.jobsite`);
    }

    const allUnits = existingData?.map((u: any) => normalize(u?.unitName));
    const isDuplicate = allUnits?.includes(normalize(value));

    if (isDuplicate) {
      setError(`${name}.unitName`, {
        type: 'manual',
        message: 'Lot/Unit name already exists.',
      });
    } else {
      clearErrors(`${name}.unitName`);
    }
  };

  const handleJobnameNameChange = (e: any) => {
    jobsiteRef.current = e;

    const allUnits = existingData?.map((u: any) => normalize(u?.jobsite));

    const isDuplicate = allUnits?.includes(normalize(e));

    if (isDuplicate) {
      setError(`${name}.jobsite`, {
        type: 'manual',
        message: 'Jobsite already exists.',
      });
    } else {
      clearErrors(`${name}.jobsite`);
    }
  };

  const handleJobNumberChange = (value: string) => {
    const allJobNumbers = existingData?.map((u: any) => normalize(u?.jobNumber));
    const isDuplicate = allJobNumbers?.includes(normalize(value));
    setJobNumberWarning(isDuplicate ? 'Builder Job Number already exists.' : null);
  };

  const handleAddressChange = (value: string) => {
    const allUnits = existingData?.map((u: any) => normalize(u?.address));
    const isDuplicate = allUnits?.includes(normalize(value));

    if (isDuplicate) {
      setError(`${name}.address`, {
        type: 'manual',
        message: 'Address already exists.',
      });
      return;
    }

    const abbrError = getAddressAbbreviationError(value);

    if (abbrError) {
      setError(`${name}.address`, {
        type: 'manual',
        message: abbrError,
      });
    } else {
      clearErrors(`${name}.address`);
    }
  };

  // const isProjectTypeRES = projectTypeValue === "RES";
  const isProjectTypeRES = true;

  return (
    <Card elevation={0} className="floorUnitBlockWrapper" style={{ marginBottom: '0', background: 'none' }}>
      <CardContent className="  " style={{ paddingTop: '4px', paddingBottom: '4px' }}>
        <Grid container spacing={2}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={2}>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput
                  // disabled={true}
                  name={`${name}.jobsite`}
                  label={'Jobsite'}
                  isCustomError={true}
                  onChange={(e: any) => handleJobnameNameChange(e)}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                      getValues('projectID'),
                      // getValues(`${name}.address`),
                      '',
                      getValues(`${name}.unitID`),
                      // getValues(`${name}.unitName`),
                      '',
                      value
                    )
                  }
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput
                  name={`${name}.unitName`}
                  disabled={isLoading}
                  label={'Lot/Unit'}
                  onChange={(e: any) => handleUnitNameChange(e)}
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput
                  name={`${name}.jobNumber`}
                  label={'Builder Job Number'}
                  onChange={(e: any) => handleJobNumberChange(e)}
                />
                {jobNumberWarning && (
                  <p style={{ color: '#ed6c02', fontSize: '0.75rem', margin: '2px 0 0 0' }}>{jobNumberWarning}</p>
                )}
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput name={`${name}.address`} label="Address" onChange={(e: any) => handleAddressChange(e)} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput name={`${name}.city`} label="City" />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormAutoCompleteSelectDynamic
                  name={`${name}.state`}
                  label="State"
                  getQueryKeyConfig={() => stateApiKeyStore.getStates()}
                  optionsValueKey="stateID"
                  optionsLabelKey="stateName"
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormFormattedInput name={`${name}.zipcode`} label="Zip Code" format={ZIPCODE_FORMAT} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormSelectDynamic
                  name={`${name}.county`}
                  label="County"
                  getQueryKeyConfig={() => countyApiKeyStore.getCounties()}
                  optionsValueKey="countyID"
                  optionsLabelKeys={['countyName']}
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormAutoCompleteSelectDynamic
                  name={`${name}.projectMunicipalities`}
                  label="Municipality"
                  disabled={!watchValues?.unitData?.state}
                  getQueryKeyConfig={() =>
                    municipalityApiKeyStore.getMunicipalityByStateID(watchValues?.unitData?.state?.stateID)
                  }
                  optionsValueKey="municipalityID"
                  optionsLabelKey="muncipalityName"
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput name={`${name}.squareFeet`} label={'Square Feet'} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormAutoCompleteSelectDynamic
                  name={`${name}.proctorResult`}
                  label="Proctor Result"
                  getQueryKeyConfig={() => proctorResultApiKeyStore.getProjectProctorResults(getValues('projectID'))}
                  optionsValueKey="proctorResultsID"
                  optionsLabelKey="proctorFileName"
                />
              </Grid>
              {isProjectTypeRES && (
                <Grid item lg={2} sm={2} xl={2} xs={2}>
                  <FormSelect
                    name={`${name}.lotType`}
                    label="Lot Type"
                    options={projectTypeValue === 'RES' ? lotTypeOption : lotTypeCMOption}
                    error={true}
                  />
                </Grid>
              )}
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput label="Building Permit#" name={`${name}.buildingPermit`} />
              </Grid>
            </Grid>
          </Grid>
          {/* <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={2}>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormCheckbox label="Create WO" name={`${name}.createWO`} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormCheckbox label="Sync With DUX" name={`${name}.syncWithDUX`} disabled={!syncWithDux} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormInput label="Building Permit#" name={`${name}.buildingPermit`} />
              </Grid>
            </Grid>
          </Grid> */}
        </Grid>
      </CardContent>
    </Card>
  );
};
